import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { PageHead, Panel } from "@/project/components/dashboard-shell";
import { stories, getInstitution } from "@/project/data/sample";
import { CheckCircle2, XCircle, Eye, Edit3, Clock, Filter, Search, BookOpen } from "lucide-react";

export const Route = createFileRoute("/admin/stories")({ component: AdminStories });

type Status = "pending" | "approved" | "rejected";

type StoryRow = {
  id: string;
  title: string;
  institutionId: string;
  date: string;
  image: string;
  excerpt: string;
  status: Status;
};

function AdminStories() {
  const [rows, setRows] = useState<StoryRow[]>(
    stories.map((s, i) => ({
      ...s,
      status: (["pending", "approved", "pending", "rejected", "approved"][i % 5] as Status),
    }))
  );
  const [filter, setFilter] = useState<Status | "all">("all");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState<StoryRow | null>(null);

  const changeStatus = (id: string, status: Status) =>
    setRows((p) => p.map((r) => r.id === id ? { ...r, status } : r));

  const filtered = rows.filter((r) => {
    const matchStatus = filter === "all" || r.status === filter;
    const matchSearch = search === "" || r.title.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const counts = {
    pending: rows.filter((r) => r.status === "pending").length,
    approved: rows.filter((r) => r.status === "approved").length,
    rejected: rows.filter((r) => r.status === "rejected").length,
  };

  const STATUS_STYLE: Record<Status, string> = {
    pending: "bg-amber-100 text-amber-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
  };

  return (
    <>
      <PageHead
        title="Stories & Impact Updates"
        subtitle="Review and moderate stories submitted by partner institutions."
      />

      {/* Stats */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        {([["pending", "Awaiting Review", "amber"], ["approved", "Published", "green"], ["rejected", "Rejected", "red"]] as const).map(([key, label, color]) => (
          <button
            key={key}
            onClick={() => setFilter(filter === key ? "all" : key)}
            className={`rounded-2xl border p-4 text-left transition ${filter === key ? `border-${color}-300 bg-${color}-50` : "border-border/60 bg-card hover:border-border"}`}
          >
            <p className={`font-display text-2xl font-bold ${color === "amber" ? "text-amber-600" : color === "green" ? "text-green-600" : "text-red-600"}`}>
              {counts[key]}
            </p>
            <p className="text-xs text-foreground/60 mt-0.5">{label}</p>
          </button>
        ))}
      </div>

      <Panel title="Submitted Stories" action={
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/20 px-3 py-1.5">
            <Search className="h-3.5 w-3.5 text-foreground/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…" className="bg-transparent text-xs outline-none w-28 placeholder:text-foreground/40" />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value as Status | "all")}
            className="rounded-full border border-border/60 bg-white px-3 py-1.5 text-xs outline-none">
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      }>
        {filtered.length === 0 ? (
          <div className="py-12 text-center text-foreground/50 text-sm">No stories match your filter.</div>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((s) => {
              const inst = getInstitution(s.institutionId);
              return (
                <li key={s.id} className="py-4">
                  <div className="flex items-start gap-4">
                    <img src={s.image} alt="" className="h-16 w-24 shrink-0 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold leading-tight">{s.title}</p>
                          <p className="mt-0.5 text-xs text-foreground/60">{inst?.name} · {s.date}</p>
                        </div>
                        <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold capitalize ${STATUS_STYLE[s.status]}`}>
                          {s.status === "pending" && <Clock className="inline h-3 w-3 mr-0.5" />}
                          {s.status === "approved" && <CheckCircle2 className="inline h-3 w-3 mr-0.5" />}
                          {s.status === "rejected" && <XCircle className="inline h-3 w-3 mr-0.5" />}
                          {s.status}
                        </span>
                      </div>
                      <p className="mt-1.5 text-sm text-foreground/70 line-clamp-2">{s.excerpt}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {s.status !== "approved" && (
                          <button onClick={() => changeStatus(s.id, "approved")}
                            className="inline-flex items-center gap-1.5 rounded-full bg-green-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-green-700 transition">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Approve & Publish
                          </button>
                        )}
                        {s.status !== "rejected" && (
                          <button onClick={() => changeStatus(s.id, "rejected")}
                            className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition">
                            <XCircle className="h-3.5 w-3.5" /> Reject
                          </button>
                        )}
                        <button onClick={() => setPreview(s)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-xs font-semibold hover:bg-muted transition">
                          <Eye className="h-3.5 w-3.5" /> Preview
                        </button>
                        <button className="inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-xs font-semibold hover:bg-muted transition">
                          <Edit3 className="h-3.5 w-3.5" /> Edit
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      {/* ── Preview Modal ── */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreview(null)}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <img src={preview.image} alt="" className="aspect-[2/1] w-full object-cover rounded-t-3xl" />
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold capitalize ${STATUS_STYLE[preview.status]}`}>{preview.status}</span>
                <button onClick={() => setPreview(null)} className="text-foreground/50 hover:text-foreground text-sm">✕ Close</button>
              </div>
              <h2 className="font-display text-xl font-bold text-[var(--maroon-deep)]">{preview.title}</h2>
              <p className="mt-1 text-xs text-foreground/60">{getInstitution(preview.institutionId)?.name} · {preview.date}</p>
              <p className="mt-4 text-sm leading-relaxed text-foreground/80">{preview.excerpt}</p>
              <div className="mt-6 flex gap-3">
                {preview.status !== "approved" && (
                  <button onClick={() => { changeStatus(preview.id, "approved"); setPreview(null); }}
                    className="flex-1 rounded-full bg-green-600 py-2.5 text-xs font-bold text-white hover:bg-green-700 transition">
                    ✓ Approve & Publish
                  </button>
                )}
                {preview.status !== "rejected" && (
                  <button onClick={() => { changeStatus(preview.id, "rejected"); setPreview(null); }}
                    className="flex-1 rounded-full border border-red-200 bg-red-50 py-2.5 text-xs font-bold text-red-600 hover:bg-red-100 transition">
                    ✕ Reject
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}